/**
 * useDealProposalsRealtime - Tempo real da aba "Propostas" do modal de negócio
 *
 * Subscreve Postgres Changes em `crm_proposals` filtrando pelo negócio aberto,
 * garantindo que a situação exibida na listagem (e o contador da aba) reflita
 * mudanças feitas fora da tela: aceite/recusa pela landing pública, expiração
 * automática, worker de geração de PDF, etc.
 *
 * Ao receber qualquer evento, invalida:
 *   - ['deal-proposals']        -> listagem da aba
 *   - ['deal-proposal-details'] -> modal de detalhes (quando aberto)
 *   - ['deal-modal-data']       -> contadores das abas do modal de negócio
 *
 * Observações de implementação:
 *  - Canal criado dentro de useEffect com removeChannel no cleanup (evita leak);
 *  - Nome de canal único por montagem, para não colidir com canais cacheados.
 *
 * CHANGELOG:
 *            o aceite/recusa feito pela landing pública sem precisar de F5
 *   RELEASE: 2026-08-12 v1.2.0 - Invalida também a timeline de atividades do
 *            negócio (eventos de proposta gravados em crm_deal_activities)
 *   RELEASE: 2026-08-12 v1.1.0 - Invalida também ['deal-has-accepted-proposal'],
 *            para a aba Produtos travar/destravar imediatamente após aceite,
 *            recusa ou cancelamento da proposta
 *   RELEASE: 2026-08-12 v1.0.0 - Criação (situação da proposta em tempo real)
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger dedicado (sistema de logging condicional do projeto)
const logger = createLogger('DealProposalsRealtime');

/**
 * Ativa o tempo real das propostas do negócio informado.
 *
 * @param dealId  - negócio aberto (null/undefined = não subscreve)
 * @param enabled - só subscreve quando a aba estiver ativa
 */
export function useDealProposalsRealtime(
  dealId: string | null | undefined,
  enabled: boolean = true,
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Sem negócio ou aba inativa: nenhum canal é criado
    if (!dealId || !enabled) return;

    const channelName = `deal-proposals-rt-${dealId}-${crypto.randomUUID()}`;
    logger.debug('Iniciando Realtime das propostas do negócio', { dealId });

    /** Invalida listagem, detalhes, contadores, trava de produtos e timeline */
    const invalidate = () => {
      logger.debug('Realtime: proposta alterada', { dealId });
      queryClient.invalidateQueries({ queryKey: ['deal-proposals'] });
      queryClient.invalidateQueries({ queryKey: ['deal-proposal-details'] });
      queryClient.invalidateQueries({ queryKey: ['deal-modal-data'] });
      // v1.1.0: bloqueio de edição de produtos depende de haver proposta aceita
      queryClient.invalidateQueries({ queryKey: ['deal-has-accepted-proposal'] });
      // v1.2.0: eventos de proposta aparecem na timeline de atividades do negócio
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities-paginated'] });
      queryClient.invalidateQueries({ queryKey: ['crm-deal-activities'] });
    };

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_proposals',
          filter: `deal_id=eq.${dealId}`,
        },
        invalidate,
      )
      .subscribe((status) => {
        logger.debug('Status da subscription de propostas', { status });
      });

    // Cleanup ao trocar de negócio / sair da aba / fechar o modal
    return () => {
      logger.debug('Removendo Realtime das propostas', { dealId });
      supabase.removeChannel(channel);
    };
  }, [dealId, enabled, queryClient]);
}
